import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { colors, radius } from '../theme';

// Video Lirik (2026-10-19): lagu + gambar latar + lirik → sinkron otomatis di server (Whisper) → render di PC
// lewat Starhub Studio (berbasis TNC7 Visualizer; tombol "Render di PC") → MP4 otomatis kembali ke Starhub Creative sebagai proyek UGC
// "video jadi" (SEO, channel & jadwal diatur di halaman UGC). Backend: routes/lirik.js + routes/lirik-bridge.js.
const API = 'https://api.starhubcreative.id';
const STUDIO = 'https://studio.starhubcreative.id/';

const dur = (s) => { const n = Math.round(Number(s) || 0); return `${Math.floor(n / 60)}:${String(n % 60).padStart(2, '0')}`; };
const umur = (iso) => { const h = (Date.now() - new Date(iso).getTime()) / 3600e3; return h < 1 ? 'baru saja' : h < 48 ? `${Math.round(h)} jam lalu` : `${Math.round(h / 24)} hari lalu`; };
const card = { background: colors.bgCard, border: `1px solid ${colors.border}`, borderRadius: radius.lg, padding: 16 };
const btn = { padding: '7px 12px', borderRadius: 8, border: `1px solid ${colors.borderStrong}`, background: colors.bgCard, color: colors.textPrimary, fontSize: 12, fontWeight: 600, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 };
const btnUtama = { ...btn, background: colors.accent, borderColor: colors.accent, color: '#fff' };
const inp = { width: '100%', boxSizing: 'border-box', padding: '9px 10px', fontSize: 13, borderRadius: 8, border: `1px solid ${colors.borderStrong}`, background: colors.bgCard, color: colors.textPrimary, fontFamily: 'inherit' };
const label = { fontSize: 12, fontWeight: 600, color: colors.textSecondary, display: 'block', marginBottom: 4 };
const pill = (c, bg) => ({ fontSize: 11, fontWeight: 700, padding: '3px 8px', borderRadius: 6, background: bg, color: c, display: 'inline-flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap' });

const CONTOH_LIRIK = `[Verse 1]
The porch light's on, the coffee's cold
...

[Chorus]
Like you never left, I still set two cups
...`;

// ===== Video Narasi (2026-10-21): suara AI gratis (edge-tts, server) =====
let namaBahasaLirik = null;
try { namaBahasaLirik = new Intl.DisplayNames(['id'], { type: 'language' }); } catch (e) { /* peramban lama */ }
const URUT_LOCALE = ['id-ID', 'ms-MY', 'en-US', 'en-GB', 'en-AU', 'pt-BR', 'es-MX', 'es-ES', 'hi-IN', 'ja-JP', 'ko-KR', 'ar-SA'];
const namaSuara = v => String(v || '').replace(/^[a-z]{2,3}-[A-Z]{2,}-/, '').replace(/(Multilingual)?Neural$/, '');
let cacheSuara = null;
function useSuara(aktif) {
  const [d, setD] = useState(cacheSuara);
  useEffect(() => {
    if (!aktif || cacheSuara) return;
    axios.get(`${API}/api/lirik/suara`).then(r => { cacheSuara = r.data; setD(r.data); }).catch(() => {});
  }, [aktif]);
  return d;
}
function PilihSuara({ voices, value, onChange }) {
  const grup = {};
  (voices || []).forEach(v => { (grup[v.locale] = grup[v.locale] || []).push(v); });
  const locs = Object.keys(grup).sort((a, b) => {
    const ia = URUT_LOCALE.indexOf(a), ib = URUT_LOCALE.indexOf(b);
    return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib) || a.localeCompare(b);
  });
  const labelLoc = l => { let n = l; try { n = (namaBahasaLirik && namaBahasaLirik.of(l)) || l; } catch (e) { /* abaikan */ } return `${n} (${l})`; };
  return (
    <select style={inp} value={value} onChange={e => onChange(e.target.value)} disabled={!voices}>
      {!voices && <option>Memuat daftar suara…</option>}
      {locs.map(l => (
        <optgroup key={l} label={labelLoc(l)}>
          {grup[l].map(v => <option key={v.name} value={v.name}>{namaSuara(v.name)} — {v.gender === 'Female' ? 'wanita' : 'pria'}</option>)}
        </optgroup>
      ))}
    </select>
  );
}
function TombolContoh({ voice, rate, teks }) {
  const [busy, setBusy] = useState(false), [err, setErr] = useState('');
  const auRef = useRef(null);
  const putar = async () => {
    setBusy(true); setErr('');
    try {
      const contoh = (String(teks || '').replace(/\s+/g, ' ').trim().slice(0, 220)) || 'Halo, ini contoh suara narator untuk video narasi.';
      const r = await axios.post(`${API}/api/lirik/tts-contoh`, { voice, rate, text: contoh }, { responseType: 'blob', timeout: 60000 });
      const u = URL.createObjectURL(r.data);
      if (auRef.current) { auRef.current.src = u; auRef.current.play().catch(() => {}); }
    } catch (e) { setErr('Contoh gagal dibuat'); }
    setBusy(false);
  };
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
      <button type="button" style={btn} onClick={putar} disabled={busy || !voice}><i className={`ti ${busy ? 'ti-loader-2' : 'ti-player-play'}`} />{busy ? 'Membuat contoh…' : 'Dengarkan contoh'}</button>
      <audio ref={auRef} controls style={{ height: 32, maxWidth: 260 }} />
      {err && <span style={{ fontSize: 12, color: colors.danger }}>{err}</span>}
    </span>
  );
}

function StatusLirik({ p }) {
  if (p.jenis === 'narasi') {
    if (p.lrc_status === 'siap') return <span style={pill(colors.success, colors.successMuted)}><i className="ti ti-check" />Suara & teks siap</span>;
    if (p.lrc_status === 'proses') return <span style={pill(colors.accentText, colors.accentMuted)}><i className="ti ti-loader-2" />Membuat suara…</span>;
    if (p.lrc_status === 'antre') return <span style={pill(colors.textSecondary, colors.bgCardMuted)}><i className="ti ti-clock-play" />Antre membuat suara{p.antre_ke ? ` (ke-${p.antre_ke})` : ''}</span>;
    return <span style={pill(colors.danger, colors.dangerMuted)} title={p.lrc_error || ''}><i className="ti ti-alert-triangle" />Gagal membuat suara</span>;
  }
  if (p.lrc_status === 'siap') return <span style={pill(colors.success, colors.successMuted)}><i className="ti ti-check" />Lirik tersinkron{p.lrc_cakupan != null ? ` · ${Math.round(p.lrc_cakupan * 100)}% kata cocok` : ''}</span>;
  if (p.lrc_status === 'proses') return <span style={pill(colors.accentText, colors.accentMuted)}><i className="ti ti-loader-2" />Menyinkronkan lirik…</span>;
  if (p.lrc_status === 'antre') return <span style={pill(colors.textSecondary, colors.bgCardMuted)}><i className="ti ti-clock-play" />Antre sinkron{p.antre_ke ? ` (ke-${p.antre_ke})` : ''}</span>;
  return <span style={pill(colors.danger, colors.dangerMuted)} title={p.lrc_error || ''}><i className="ti ti-alert-triangle" />Sinkron gagal</span>;
}
function StatusRender({ p }) {
  const m = {
    belum: [colors.textSecondary, colors.bgCardMuted, 'ti-device-desktop', 'Belum dirender'],
    menunggu: [colors.warning, colors.warningMuted, 'ti-clock-play', 'Menunggu Starhub Studio dibuka'],
    render: [colors.accentText, colors.accentMuted, 'ti-loader-2', `Dirender di PC${p.render_progress != null ? ` ${p.render_progress}%` : ''}`],
    upload: [colors.accentText, colors.accentMuted, 'ti-upload', `Mengunggah${p.render_progress != null ? ` ${p.render_progress}%` : ''}`],
    selesai: [colors.success, colors.successMuted, 'ti-movie', 'Video jadi → UGC'],
    gagal: [colors.danger, colors.dangerMuted, 'ti-alert-triangle', 'Render gagal'],
  }[p.render_status] || [colors.textSecondary, colors.bgCardMuted, 'ti-device-desktop', p.render_status];
  return <span style={pill(m[0], m[1])} title={p.render_error || ''}><i className={`ti ${m[2]}`} />{m[3]}</span>;
}

// Terjemahan teks narasi (2026-10-21): Argos Translate di server (gratis, offline, tanpa token). Hasil mesin →
// menggantikan isi kotak teks untuk dicek/diedit; teks asli bisa dikembalikan. Suara pindah ke bahasa tujuan.
const LOCALE_BAHASA = { id: 'id-ID', en: 'en-US', ms: 'ms-MY', ar: 'ar-SA', es: 'es-ES', fr: 'fr-FR', de: 'de-DE', pt: 'pt-BR', ja: 'ja-JP', ko: 'ko-KR',
  zh: 'zh-CN', hi: 'hi-IN', ru: 'ru-RU', th: 'th-TH', vi: 'vi-VN', tr: 'tr-TR', tl: 'fil-PH', it: 'it-IT', nl: 'nl-NL' };
const namaBhs = c => { try { const n = namaBahasaLirik && namaBahasaLirik.of(c === 'tl' ? 'fil' : c); return n ? n[0].toUpperCase() + n.slice(1) : c; } catch (e) { return c; } };
let cacheBahasa = null;
function PanelTerjemah({ teks, setTeks, voices, voice, setVoice }) {
  const [bhs, setBhs] = useState(cacheBahasa);
  const [dari, setDari] = useState('id'), [ke, setKe] = useState('en');
  const [jalan, setJalan] = useState(null), [err, setErr] = useState(''), [asli, setAsli] = useState(null);
  const henti = useRef(false);
  useEffect(() => {
    if (!cacheBahasa) axios.get(`${API}/api/lirik/terjemah/bahasa`).then(r => { cacheBahasa = r.data; setBhs(r.data); }).catch(() => setErr('Daftar bahasa tidak bisa dimuat'));
    return () => { henti.current = true; };
  }, []);
  const pilihSuara = kode => {
    const loc = LOCALE_BAHASA[kode]; if (!voices || !loc) return;
    if (String(voice || '').startsWith(loc)) return;
    const v = voices.find(x => x.locale === loc && x.gender === 'Female') || voices.find(x => x.locale === loc) || voices.find(x => x.locale.startsWith(kode + '-'));
    if (v) setVoice(v.name);
  };
  const mulai = async () => {
    setErr('');
    if (teks.trim().length < 2) { setErr('Tempel teks dulu'); return; }
    setJalan({ progress: 0, antreDepan: 0 });
    try {
      const { data } = await axios.post(`${API}/api/lirik/terjemah`, { dari, ke, teks });
      for (;;) {
        await new Promise(r => setTimeout(r, 2000));
        if (henti.current) return;
        const { data: j } = await axios.get(`${API}/api/lirik/terjemah/${data.id}`);
        if (j.status === 'selesai') { setAsli(a => a ?? teks); setTeks(j.hasil); pilihSuara(ke); break; }
        if (j.status === 'gagal') { setErr(j.error || 'Terjemahan gagal'); break; }
        setJalan({ progress: j.progress, antreDepan: j.antreDepan, antre: j.status === 'antre' });
      }
    } catch (e) { setErr(e.response?.data?.error || 'Terjemahan gagal'); }
    setJalan(null);
  };
  const kembalikan = () => { setTeks(asli); setAsli(null); pilihSuara(dari); };
  const opsi = (list, nilai, set, kecuali) => (
    <select style={{ ...inp, width: 'auto', minWidth: 140 }} value={nilai} onChange={e => set(e.target.value)} disabled={!bhs || !!jalan}>
      {(list || [nilai]).filter(c => c !== kecuali).sort((a, b) => namaBhs(a).localeCompare(namaBhs(b), 'id')).map(c => <option key={c} value={c}>{namaBhs(c)}</option>)}
    </select>
  );
  return (
    <div style={{ border: `1px dashed ${colors.borderStrong}`, borderRadius: 8, padding: 10, display: 'grid', gap: 8 }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <span style={{ ...label, marginBottom: 0 }}><i className="ti ti-language" style={{ color: colors.accent }} /> Terjemahkan teks</span>
        {opsi(bhs && bhs.filter(c => c === 'id' || c === 'en'), dari, v => { setDari(v); if (v === ke) setKe(v === 'en' ? 'id' : 'en'); }, null)}
        <i className="ti ti-arrow-right" style={{ color: colors.textSecondary }} />
        {opsi(bhs, ke, setKe, dari)}
        <button type="button" style={btn} onClick={mulai} disabled={!bhs || !!jalan}>
          <i className={`ti ${jalan ? 'ti-loader-2' : 'ti-language'}`} />{jalan ? (jalan.antre ? `Antre${jalan.antreDepan ? ` (${jalan.antreDepan} di depan)` : ''}…` : `Menerjemahkan ${jalan.progress}%…`) : 'Terjemahkan'}
        </button>
        {asli != null && !jalan && <button type="button" style={btn} onClick={kembalikan}><i className="ti ti-arrow-back-up" />Kembalikan teks asli</button>}
      </div>
      {jalan && <div style={{ height: 4, borderRadius: 2, background: colors.border, overflow: 'hidden' }}><div style={{ width: `${jalan.progress}%`, height: '100%', background: colors.accent, transition: 'width .5s' }} /></div>}
      <div style={{ fontSize: 11.5, color: err ? colors.danger : colors.textSecondary }}>
        {err || (asli != null
          ? 'Hasil terjemahan mesin sudah menggantikan teks di atas — baca & rapikan dulu (nama, kata ganti, istilah) sebelum membuat suara. Suara narator ikut dipindah ke bahasa tujuan.'
          : 'Gratis & offline (Argos Translate). ±1–3 menit untuk 20.000 karakter. Hasil mesin: cocok untuk draf, tetap perlu dicek manual.')}
      </div>
    </div>
  );
}

function FormBaru({ onDibuat }) {
  const [f, setF] = useState({ title: '', lyrics: '', preset: (() => { try { return localStorage.getItem('lirik_preset') || ''; } catch (e) { return ''; } })(), description: '', tags: '', synthetic: true });
  const [audio, setAudio] = useState(null), [image, setImage] = useState(null), [thumb, setThumb] = useState(null);
  const [kirim, setKirim] = useState(null), [err, setErr] = useState('');
  const [jenis, setJenis] = useState('lagu');
  const [voice, setVoice] = useState(() => { try { return localStorage.getItem('narasi_suara') || 'id-ID-GadisNeural'; } catch (e) { return 'id-ID-GadisNeural'; } });
  const [rate, setRate] = useState(0);
  const narasi = jenis === 'narasi';
  const voices = useSuara(narasi);
  const MAKS = 20000;
  const upd = (k, v) => setF(p => ({ ...p, [k]: v }));
  const simpan = async () => {
    setErr('');
    if (narasi) {
      if (!f.title.trim() || !image || f.lyrics.trim().length < 20) { setErr('Judul, gambar latar, dan teks narasi wajib diisi.'); return; }
      if (f.lyrics.length > MAKS) { setErr(`Teks maksimal ${MAKS.toLocaleString('id-ID')} karakter per proyek. Pecah per bab.`); return; }
    } else if (!f.title.trim() || !audio || !image || f.lyrics.trim().length < 20) { setErr('Judul, lagu, gambar latar, dan lirik wajib diisi.'); return; }
    const fd = new FormData();
    Object.entries(f).forEach(([k, v]) => fd.append(k, String(narasi && k === 'synthetic' ? true : v)));
    if (narasi) { fd.append('jenis', 'narasi'); fd.append('voice', voice); fd.append('rate', String(rate)); try { localStorage.setItem('narasi_suara', voice); } catch (e) { /* abaikan */ } }
    else fd.append('audio', audio);
    fd.append('image', image); if (thumb) fd.append('thumbnail', thumb);
    try { localStorage.setItem('lirik_preset', f.preset); } catch (e) { /* abaikan */ }
    setKirim(0);
    try {
      await axios.post(`${API}/api/lirik`, fd, { timeout: 0, onUploadProgress: e => e.total && setKirim(Math.round((e.loaded / e.total) * 100)) });
      setF(p => ({ ...p, title: '', lyrics: '', description: '' })); setAudio(null); setImage(null); setThumb(null);
      onDibuat();
    } catch (e) { setErr(e.response?.data?.error || 'Gagal membuat proyek'); }
    setKirim(null);
  };
  const fileBox = (lbl, accept, val, set, wajib) => (
    <div style={{ flex: '1 1 200px', minWidth: 0 }}>
      <span style={label}>{lbl}{wajib ? ' *' : ''}</span>
      <input type="file" accept={accept} onChange={e => set(e.target.files[0] || null)} style={{ ...inp, padding: 7 }} />
      {val && <div style={{ fontSize: 11, color: colors.textSecondary, marginTop: 3 }}>{val.name} · {(val.size / 1048576).toFixed(1)} MB</div>}
    </div>
  );
  return (
    <div style={{ ...card, display: 'grid', gap: 12 }}>
      <div style={{ fontSize: 15, fontWeight: 700, color: colors.textPrimary }}><i className="ti ti-file-music" style={{ color: colors.accent }} /> Proyek baru</div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {[['lagu', 'ti-music', 'Lagu (unggah lagu)'], ['narasi', 'ti-microphone-2', 'Narasi (suara AI)']].map(([k, ic, t]) => (
          <button key={k} type="button" onClick={() => setJenis(k)}
            style={{ ...btn, background: jenis === k ? colors.accentMuted : colors.bgCard, color: jenis === k ? colors.accentText : colors.textSecondary, borderColor: jenis === k ? colors.accent : colors.border }}>
            <i className={`ti ${ic}`} />{t}
          </button>
        ))}
      </div>
      <div><span style={label}>{narasi ? 'Judul video *' : 'Judul lagu *'}</span><input style={inp} value={f.title} maxLength={100} onChange={e => upd('title', e.target.value)} placeholder={narasi ? 'Bab 1 — Kabut di Lereng Bukit' : 'Like You Never Left'} /></div>
      {narasi && (
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div style={{ flex: '2 1 260px', minWidth: 0 }}><span style={label}>Suara narator *</span><PilihSuara voices={voices} value={voice} onChange={setVoice} /></div>
          <div style={{ flex: '1 1 180px', minWidth: 0 }}>
            <span style={label}>Kecepatan bicara: {rate > 0 ? '+' : ''}{rate}%</span>
            <input type="range" min={-40} max={40} step={5} value={rate} onChange={e => setRate(Number(e.target.value))} style={{ width: '100%', padding: 0 }} />
          </div>
          <div style={{ flex: '1 1 100%' }}><TombolContoh voice={voice} rate={rate} teks={f.lyrics} /></div>
        </div>
      )}
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        {!narasi && fileBox('Lagu (MP3/WAV, maks 15 menit)', 'audio/*,.mp3,.wav,.m4a,.flac', audio, setAudio, true)}
        {fileBox('Gambar latar video (16:9)', 'image/*', image, setImage, true)}
        {fileBox('Thumbnail YouTube (opsional)', 'image/*', thumb, setThumb, false)}
      </div>
      <div>
        {narasi
          ? <span style={label}>Teks narasi * <span style={{ fontWeight: 400 }}>— akan dibacakan & tampil sebagai teks berjalan. Pisahkan paragraf dengan baris kosong. {f.lyrics.length.toLocaleString('id-ID')}/{MAKS.toLocaleString('id-ID')} karakter (±1 menit per 1.000 karakter).</span></span>
          : <span style={label}>Lirik * <span style={{ fontWeight: 400 }}>— tempel lirik final. Tag seperti [Verse]/[Chorus] boleh, akan diabaikan saat tampil.</span></span>}
        <textarea style={{ ...inp, minHeight: narasi ? 260 : 180, resize: 'vertical', borderColor: narasi && f.lyrics.length > MAKS ? colors.danger : undefined }} value={f.lyrics} onChange={e => upd('lyrics', e.target.value)}
          placeholder={narasi ? 'Bab Satu: Kabut di Lereng Bukit\n\nPada suatu pagi yang dingin, Rani membuka jendela kamarnya…' : CONTOH_LIRIK} />
        {narasi && <div style={{ marginTop: 8 }}><PanelTerjemah teks={f.lyrics} setTeks={v => upd('lyrics', v)} voices={voices} voice={voice} setVoice={setVoice} /></div>}
      </div>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 200px' }}><span style={label}>Nama preset Starhub Studio (opsional)</span><input style={inp} value={f.preset} onChange={e => upd('preset', e.target.value)} placeholder="Romantic Ballad" /></div>
        <div style={{ flex: '2 1 280px' }}><span style={label}>Tags (pisah koma, opsional)</span><input style={inp} value={f.tags} onChange={e => upd('tags', e.target.value)} placeholder="romantic love songs, love songs 80s 90s, emotional love songs" /></div>
      </div>
      <div><span style={label}>Deskripsi YouTube (opsional — bisa diisi/di-generate nanti di UGC)</span><textarea style={{ ...inp, minHeight: 70, resize: 'vertical' }} value={f.description} onChange={e => upd('description', e.target.value)} /></div>
      <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: colors.textPrimary, cursor: 'pointer' }}>
        <input type="checkbox" checked={narasi || f.synthetic} disabled={narasi} onChange={e => upd('synthetic', e.target.checked)} style={{ width: 16, height: 16, padding: 0, margin: 0, flex: '0 0 auto' }} />
        Tandai sebagai konten sintetis/AI di YouTube <span style={{ color: colors.textSecondary, fontSize: 12 }}>{narasi ? '(selalu aktif: suara narator dibuat AI)' : '(wajib untuk lagu buatan AI; tidak memengaruhi monetisasi)'}</span>
      </label>
      {err && <div style={{ color: colors.danger, fontSize: 13 }}>{err}</div>}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <button style={btnUtama} disabled={kirim != null} onClick={simpan}><i className={`ti ${kirim != null ? 'ti-loader-2' : 'ti-upload'}`} />{kirim != null ? `Mengunggah ${kirim}%…` : narasi ? 'Buat proyek & suara narasi' : 'Buat proyek & sinkronkan lirik'}</button>
        <span style={{ fontSize: 12, color: colors.textSecondary }}>{narasi ? 'Suara & teks tersinkron dibuat otomatis di server (biasanya kurang dari 1 menit).' : 'Sinkron lirik berjalan otomatis di server (beberapa menit per lagu).'}</span>
      </div>
    </div>
  );
}

// Editor LRC: lirik tersinkron bisa dirapikan manual. "Pakai waktu saat ini" mengisi waktu baris di posisi kursor.
function EditorLrc({ id, onTutup, onBerubah }) {
  const [d, setD] = useState(null), [lrc, setLrc] = useState(''), [lirik, setLirik] = useState(''), [tab, setTab] = useState('lrc');
  const [audioUrl, setAudioUrl] = useState(null), [msg, setMsg] = useState(''), [busy, setBusy] = useState(false);
  const taRef = useRef(null), auRef = useRef(null);
  useEffect(() => {
    axios.get(`${API}/api/lirik/${id}`).then(r => { setD(r.data); setLrc(r.data.lrc_text || ''); setLirik(r.data.lyrics_text || ''); }).catch(() => setMsg('Gagal memuat proyek'));
    let url = null;
    axios.get(`${API}/api/lirik/${id}/audio`, { responseType: 'blob', timeout: 0 }).then(r => { url = URL.createObjectURL(r.data); setAudioUrl(url); }).catch(() => {});
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [id]);
  const pakaiWaktu = () => {
    const ta = taRef.current, a = auRef.current; if (!ta || !a) return;
    const t = a.currentTime, m = Math.floor(t / 60), s = (t - m * 60).toFixed(2).padStart(5, '0');
    const tag = `[${String(m).padStart(2, '0')}:${s}]`;
    const awal = lrc.lastIndexOf('\n', ta.selectionStart - 1) + 1, akhir = lrc.indexOf('\n', ta.selectionStart);
    const baris = lrc.slice(awal, akhir < 0 ? undefined : akhir).replace(/^\[\d{1,3}:\d{2}(\.\d{1,3})?\]/, '');
    const baru = lrc.slice(0, awal) + tag + baris + (akhir < 0 ? '' : lrc.slice(akhir));
    setLrc(baru);
    const lanjut = baru.indexOf('\n', awal) + 1; setTimeout(() => { ta.focus(); if (lanjut > 0) ta.setSelectionRange(lanjut, lanjut); }, 0);
  };
  const simpanLrc = async () => { setBusy(true); setMsg(''); try { await axios.patch(`${API}/api/lirik/${id}`, { lrc_text: lrc }); setMsg('LRC disimpan.'); onBerubah(); } catch (e) { setMsg(e.response?.data?.error || 'Gagal menyimpan'); } setBusy(false); };
  const narasi = d && d.jenis === 'narasi';
  const [voice, setVoice] = useState(null), [rate, setRate] = useState(0);
  useEffect(() => { if (d && d.jenis === 'narasi') { setVoice(d.tts_voice); setRate(d.tts_rate || 0); } }, [d]);
  const voices = useSuara(narasi);
  const sinkronUlang = async () => {
    setBusy(true); setMsg('');
    try {
      await axios.patch(`${API}/api/lirik/${id}`, narasi ? { lyrics_text: lirik, tts_voice: voice, tts_rate: rate } : { lyrics_text: lirik });
      await axios.post(`${API}/api/lirik/${id}/sync`);
      setMsg(narasi ? 'Teks & suara disimpan. Suara sedang dibuat ulang.' : 'Lirik disimpan & dimasukkan antrean sinkron.'); onBerubah();
    }
    catch (e) { setMsg(e.response?.data?.error || 'Gagal'); }
    setBusy(false);
  };
  return (
    <div onClick={onTutup} style={{ position: 'fixed', inset: 0, background: 'rgba(17,24,39,0.45)', zIndex: 1000, display: 'flex', justifyContent: 'center', alignItems: 'flex-start', padding: '30px 16px', overflowY: 'auto' }}>
      <div onClick={e => e.stopPropagation()} style={{ ...card, width: '100%', maxWidth: 760, boxShadow: '0 10px 40px rgba(0,0,0,0.2)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: colors.textPrimary }}><i className="ti ti-microphone-2" style={{ color: colors.accent }} /> Lirik · {d?.title || '…'}</div>
          <button style={{ ...btn, padding: '4px 8px' }} onClick={onTutup} aria-label="Tutup"><i className="ti ti-x" /></button>
        </div>
        {audioUrl && <audio ref={auRef} src={audioUrl} controls style={{ width: '100%', marginTop: 10 }} />}
        <div style={{ display: 'flex', gap: 6, margin: '10px 0' }}>
          {[['lrc', narasi ? 'Teks tersinkron (LRC)' : 'Lirik tersinkron (LRC)'], ['teks', narasi ? 'Teks & suara narasi' : 'Lirik asli']].map(([k, t]) => (
            <button key={k} onClick={() => setTab(k)} style={{ ...btn, background: tab === k ? colors.accentMuted : colors.bgCard, color: tab === k ? colors.accentText : colors.textSecondary, borderColor: tab === k ? colors.accent : colors.border }}>{t}</button>
          ))}
        </div>
        {tab === 'lrc' ? (
          <>
            <div style={{ fontSize: 12, color: colors.textSecondary, marginBottom: 6 }}>Putar lagu, klik baris yang waktunya meleset, lalu tekan <b>Pakai waktu saat ini</b> tepat saat baris itu mulai dinyanyikan. Format: <code>[mm:ss.xx] lirik</code>.</div>
            <textarea ref={taRef} value={lrc} onChange={e => setLrc(e.target.value)} style={{ ...inp, minHeight: 320, fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 12, resize: 'vertical' }} />
            <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
              <button style={btn} onClick={pakaiWaktu} disabled={!audioUrl}><i className="ti ti-clock-play" />Pakai waktu saat ini</button>
              <button style={btnUtama} onClick={simpanLrc} disabled={busy}><i className="ti ti-device-floppy" />Simpan LRC</button>
            </div>
          </>
        ) : (
          <>
            {narasi && (
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 8 }}>
                <div style={{ flex: '2 1 240px', minWidth: 0 }}><span style={label}>Suara narator</span><PilihSuara voices={voices} value={voice || ''} onChange={setVoice} /></div>
                <div style={{ flex: '1 1 160px', minWidth: 0 }}><span style={label}>Kecepatan: {rate > 0 ? '+' : ''}{rate}%</span><input type="range" min={-40} max={40} step={5} value={rate} onChange={e => setRate(Number(e.target.value))} style={{ width: '100%', padding: 0 }} /></div>
                <div style={{ flex: '1 1 100%' }}><TombolContoh voice={voice} rate={rate} teks={lirik} /></div>
              </div>
            )}
            <div style={{ fontSize: 12, color: colors.textSecondary, marginBottom: 6 }}>{narasi ? 'Ubah teks/suara lalu buat ulang suara. Teks tersinkron lama akan diganti.' : 'Ubah lirik lalu sinkron ulang otomatis. LRC lama akan diganti.'}</div>
            <textarea value={lirik} onChange={e => setLirik(e.target.value)} style={{ ...inp, minHeight: 320, resize: 'vertical' }} />
            <button style={{ ...btnUtama, marginTop: 8 }} onClick={sinkronUlang} disabled={busy}><i className="ti ti-refresh" />{narasi ? 'Simpan & buat ulang suara' : 'Simpan & sinkron ulang'}</button>
          </>
        )}
        {msg && <div style={{ fontSize: 13, color: colors.textPrimary, marginTop: 8 }}>{msg}</div>}
      </div>
    </div>
  );
}

export default function VideoLirik({ onOpenUgc }) {
  const [list, setList] = useState(null);
  const [baru, setBaru] = useState(false);
  const [edit, setEdit] = useState(null);
  const [info, setInfo] = useState(false);
  const [msg, setMsg] = useState('');
  const muat = useCallback(() => axios.get(`${API}/api/lirik`).then(r => setList(r.data)).catch(() => setList([])), []);
  useEffect(() => { muat(); }, [muat]);
  useEffect(() => {
    if (!list || !list.some(p => ['antre', 'proses'].includes(p.lrc_status) || ['menunggu', 'render', 'upload'].includes(p.render_status))) return undefined;
    const t = setInterval(muat, 5000); return () => clearInterval(t);
  }, [list, muat]);

  const render = async (p) => {
    const w = window.open('', '_blank'); // dibuka langsung agar tidak diblokir pemblokir pop-up
    try {
      const r = await axios.post(`${API}/api/lirik/${p.id}/render`);
      if (w) w.location.href = r.data.url; else window.location.href = r.data.url;
      setMsg('Starhub Studio terbuka di tab baru: lagu, latar & lirik dimuat otomatis, render mulai sendiri. Biarkan tab itu terbuka sampai selesai.');
      muat();
    } catch (e) { if (w) w.close(); setMsg(e.response?.data?.error || 'Gagal membuka Starhub Studio'); }
  };
  const hapus = async (p) => {
    if (!window.confirm(`Hapus proyek "${p.title}"? Video yang sudah masuk UGC tidak ikut terhapus.`)) return;
    try { await axios.delete(`${API}/api/lirik/${p.id}`); muat(); } catch (e) { /* toast global */ }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: colors.textPrimary, margin: 0 }}>Video Lirik & Narasi</h1>
          <div style={{ fontSize: 13, color: colors.textSecondary, marginTop: 4 }}>Lagu + lirik, atau teks yang dibacakan suara AI → teks tersinkron otomatis → dirender di PC lewat Starhub Studio → masuk UGC siap dijadwalkan.</div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button style={btn} onClick={() => setInfo(!info)}><i className="ti ti-info-circle" />Cara kerja</button>
          <a href={STUDIO} target="_blank" rel="noreferrer" style={{ ...btn, textDecoration: 'none' }}><i className="ti ti-download" />Pasang Starhub Studio di PC</a>
          <button style={btnUtama} onClick={() => setBaru(!baru)}><i className="ti ti-file-music" />{baru ? 'Tutup form' : 'Proyek baru'}</button>
        </div>
      </div>

      {info && (
        <div style={{ ...card, background: colors.bgCardMuted, fontSize: 13, color: colors.textPrimary, marginBottom: 14, display: 'grid', gap: 6 }}>
          <div><b>1. Buat proyek</b> — unggah lagu (dari Suno, dsb.), gambar latar 16:9, dan tempel liriknya.</div>
          <div><b>2. Sinkron otomatis</b> — server mendengarkan lagu dan memberi waktu tiap baris lirik. Bisa dirapikan manual lewat tombol <b>Lirik</b>.</div>
          <div><b>3. Render di PC</b> — tombol <b>Render di PC (Starhub Studio)</b> membuka Starhub Studio, memuat semuanya, lalu merender memakai komputer kamu (server tetap ringan). Simpan dulu gaya tampilan di Starhub Studio sebagai <b>preset</b> (mis. "Romantic Ballad") dan tulis namanya di proyek.</div>
          <div><b>4. Otomatis ke Starhub Creative</b> — setelah render, video langsung terunggah ke <b>UGC Compilation</b> sebagai video jadi (judul, deskripsi, tags, thumbnail & label AI sudah terisi). Tinggal pilih channel, jadwal, Setujui. Tombol <b>Unduh MP4</b> juga ada di Starhub Studio.</div>
          <div style={{ color: colors.textSecondary }}>Pertama kali membuka Starhub Studio akan diminta login. Pakai Chrome/Edge di PC; klik <b>Pasang App</b> di Starhub Studio agar bisa dibuka seperti aplikasi.</div>
        </div>
      )}
      {msg && <div style={{ background: colors.accentMuted, color: colors.accentText, borderRadius: 8, padding: '9px 12px', fontSize: 13, marginBottom: 12 }}>{msg}</div>}
      {baru && <div style={{ marginBottom: 14 }}><FormBaru onDibuat={() => { setBaru(false); setMsg('Proyek dibuat. Lirik sedang disinkronkan otomatis.'); muat(); }} /></div>}

      {list === null ? <div style={{ fontSize: 13, color: colors.textSecondary }}>Memuat…</div>
        : list.length === 0 ? (
          <div style={{ ...card, borderStyle: 'dashed', textAlign: 'center', color: colors.textSecondary, fontSize: 13, padding: 30 }}>
            Belum ada proyek. Klik <b>Proyek baru</b> untuk mulai.
          </div>
        ) : (
          <div style={{ display: 'grid', gap: 10 }}>
            {list.map(p => (
              <div key={p.id} style={{ ...card, display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'center' }}>
                {p.thumbnail_url
                  ? <img src={`${API}${p.thumbnail_url}`} alt="" style={{ width: 128, aspectRatio: '16 / 9', objectFit: 'cover', borderRadius: 8, flex: '0 0 auto', background: colors.bgCardMuted }} />
                  : <div style={{ width: 128, aspectRatio: '16 / 9', borderRadius: 8, background: colors.bgCardMuted }} />}
                <div style={{ flex: '1 1 240px', minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: colors.textPrimary }}>{p.title}</div>
                  <div style={{ fontSize: 12, color: colors.textSecondary, margin: '2px 0 6px' }}>{p.jenis === 'narasi' ? `🎙 Narasi · ${namaSuara(p.tts_voice)}${p.tts_rate ? ` ${p.tts_rate > 0 ? '+' : ''}${p.tts_rate}%` : ''} · ` : ''}{p.durasi ? dur(p.durasi) : '–'} · {p.preset_name ? `preset "${p.preset_name}" · ` : ''}{umur(p.created_at)}{p.synthetic ? ' · label AI' : ''}</div>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}><StatusLirik p={p} /><StatusRender p={p} /></div>
                  {p.lrc_status === 'gagal' && p.lrc_error && <div style={{ fontSize: 11, color: colors.danger, marginTop: 4 }}>{p.lrc_error}</div>}
                  {p.render_status === 'gagal' && p.render_error && <div style={{ fontSize: 11, color: colors.danger, marginTop: 4 }}>{p.render_error}</div>}
                </div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  <button style={btn} onClick={() => setEdit(p.id)}><i className="ti ti-pencil" />Lirik</button>
                  <button style={btnUtama} disabled={!(p.lrc_status === 'siap') || ['render', 'upload'].includes(p.render_status)} onClick={() => render(p)}
                    title={p.lrc_status !== 'siap' ? 'Tunggu lirik tersinkron dulu' : 'Buka Starhub Studio dan render otomatis di PC'}>
                    <i className="ti ti-device-desktop" />{p.render_status === 'selesai' ? 'Render ulang' : 'Render di PC (Starhub Studio)'}
                  </button>
                  {p.render_status === 'selesai' && onOpenUgc && <button style={btn} onClick={onOpenUgc}><i className="ti ti-movie" />Buka di UGC</button>}
                  <button style={{ ...btn, color: colors.danger }} onClick={() => hapus(p)} aria-label="Hapus"><i className="ti ti-trash" /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      {edit && <EditorLrc id={edit} onTutup={() => setEdit(null)} onBerubah={muat} />}
    </div>
  );
}
